import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import { ReportParameter } from './ReportParameter';
import type { EntityState } from './../enums/EntityState';

export class Report extends BaseObj {
    public ID: number;
    public __id: number;

    public Description: string | null;
    public __description: string | null;

    public DescriptionKey: string | null;
    public __descriptionkey: string | null;

    public Directory: string | null;
    public __directory: string | null;

    public File: string | null;
    public __file: string | null;

    public Groupingkey: string | null;
    public __groupingkey: string | null;

    public HelpLink: string | null;
    public __helplink: string | null;

    public NoDefaultFilters: boolean;
    public __nodefaultfilters: boolean;

    public ReportParameters: ReportParameter[];
    public __reportparameters: ReportParameter[];

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        Description?: string | null;
        DescriptionKey?: string | null;
        Directory?: string | null;
        EntityState?: EntityState;
        File?: string | null;
        Groupingkey?: string | null;
        HelpLink?: string | null;
        NoDefaultFilters?: boolean;
        ReportParameters?: ReportParameter[];
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<Report>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('Report tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.Description = values.Description ?? null;
        this.__description = values.Description ?? null;
        Object.defineProperty(this, 'Description', {
            get(): string | null {
                return this.__description;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Description', this.__description, value);
                this.__description = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.DescriptionKey = values.DescriptionKey ?? null;
        this.__descriptionkey = values.DescriptionKey ?? null;
        Object.defineProperty(this, 'DescriptionKey', {
            get(): string | null {
                return this.__descriptionkey;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('DescriptionKey', this.__descriptionkey, value);
                this.__descriptionkey = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Directory = values.Directory ?? null;
        this.__directory = values.Directory ?? null;
        Object.defineProperty(this, 'Directory', {
            get(): string | null {
                return this.__directory;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Directory', this.__directory, value);
                this.__directory = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.File = values.File ?? null;
        this.__file = values.File ?? null;
        Object.defineProperty(this, 'File', {
            get(): string | null {
                return this.__file;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('File', this.__file, value);
                this.__file = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Groupingkey = values.Groupingkey ?? null;
        this.__groupingkey = values.Groupingkey ?? null;
        Object.defineProperty(this, 'Groupingkey', {
            get(): string | null {
                return this.__groupingkey;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Groupingkey', this.__groupingkey, value);
                this.__groupingkey = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.HelpLink = values.HelpLink ?? null;
        this.__helplink = values.HelpLink ?? null;
        Object.defineProperty(this, 'HelpLink', {
            get(): string | null {
                return this.__helplink;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('HelpLink', this.__helplink, value);
                this.__helplink = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.NoDefaultFilters = values.NoDefaultFilters ?? false;
        this.__nodefaultfilters = values.NoDefaultFilters ?? false;
        Object.defineProperty(this, 'NoDefaultFilters', {
            get(): boolean {
                return this.__nodefaultfilters;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('NoDefaultFilters', this.__nodefaultfilters, value);
                this.__nodefaultfilters = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ReportParameters = values.ReportParameters ?? [];
        this.__reportparameters = this.__entityStateHandler.createArrayWithMutationCallback({
            initalValue: values.ReportParameters ?? [],
            callback: (_oldVal, newVal) => this.__entityStateHandler.childArrayOfBaseObjsChanged('ReportParameters', newVal),
        });
        this.__reportparameters.forEach((child) => {
            child._setNotifyParentOfModificationCallback(() =>
                this.__entityStateHandler.childArrayOfBaseObjsChanged('ReportParameters', this.ReportParameters),
            );
        });
        Object.defineProperty(this, 'ReportParameters', {
            get(): ReportParameter[] {
                return this.__reportparameters;
            },
            set(value: ReportParameter[]) {
                // TODO: Here we could validate that the new and the old value has all the same objects. If we dont we have a bug
                this.__reportparameters = this.__entityStateHandler.createArrayWithMutationCallback({
                    initalValue: value ?? [],
                    callback: (_oldVal: ReportParameter[], newVal: ReportParameter[]) =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('ReportParameters', newVal),
                });
                this.__reportparameters.forEach((child: ReportParameter) => {
                    child._setNotifyParentOfModificationCallback(() =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('ReportParameters', this.ReportParameters),
                    );
                });
                this.__entityStateHandler.childArrayOfBaseObjsChanged('ReportParameters', this.ReportParameters);
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"Description": ${JSON.stringify(this.Description)}` +
            ',' +
            `"DescriptionKey": ${JSON.stringify(this.DescriptionKey)}` +
            ',' +
            `"Directory": ${JSON.stringify(this.Directory)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"File": ${JSON.stringify(this.File)}` +
            ',' +
            `"Groupingkey": ${JSON.stringify(this.Groupingkey)}` +
            ',' +
            `"HelpLink": ${JSON.stringify(this.HelpLink)}` +
            ',' +
            `"NoDefaultFilters": ${JSON.stringify(this.NoDefaultFilters)}` +
            ',' +
            `"ReportParameters": [ ${this.ReportParameters.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of Report from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        Description?: string | null;
        DescriptionKey?: string | null;
        Directory?: string | null;
        File?: string | null;
        Groupingkey?: string | null;
        HelpLink?: string | null;
        NoDefaultFilters?: boolean;
        ReportParameters?: ReportParameter[];
        EntityState?: 'Added' | 'Unchanged';
    }): Report {
        return new Report(values);
    }

    public static _FromServerJson(json: any): Report {
        return new Report({
            ID: json.ID,
            Description: json.Description ?? '',
            DescriptionKey: json.DescriptionKey ?? '',
            Directory: json.Directory ?? '',
            File: json.File ?? '',
            Groupingkey: json.Groupingkey ?? '',
            HelpLink: json.HelpLink ?? '',
            NoDefaultFilters: json.NoDefaultFilters,
            ReportParameters: json.ReportParameters?.map((x: any) => ReportParameter._FromServerJson(x)),
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of Report from another instance of Report.
     * @param other The Report you want to clone
     * @returns A cloned Report
     */
    public static FromOther(other: Report): Report {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return Report._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return Report.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: (
            | 'ID'
            | 'Description'
            | 'DescriptionKey'
            | 'Directory'
            | 'File'
            | 'Groupingkey'
            | 'HelpLink'
            | 'NoDefaultFilters'
            | 'ReportParameters'
        )[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'Report';
}
